import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { useEffect } from "react";
import { DeltaDirection, dayjs } from "@boo/shared";
import { ArrowDown, ArrowUp, GitCompare } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { CATEGORY_LABELS } from "../../monthly-summary/types/category.types";
import { useBudgetStore } from "../store/useBudgetStore";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function fmt(n: number) {
  return n.toLocaleString("th-TH", { minimumFractionDigits: 0 });
}

function monthLabel(year: number, month: number) {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function DeltaBadge({
  delta,
  direction,
}: {
  delta: number;
  direction: DeltaDirection;
}) {
  const improved = direction === DeltaDirection.Down;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[12px] font-medium [font-variant-numeric:tabular-nums] ${
        improved ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"
      }`}
    >
      {direction === DeltaDirection.Up ? (
        <ArrowUp className="w-3 h-3 shrink-0" />
      ) : (
        <ArrowDown className="w-3 h-3 shrink-0" />
      )}
      ฿{fmt(Math.abs(delta))}
    </span>
  );
}

export default function MonthComparisonTable() {
  const now = dayjs();
  const year = now.year();
  const month = now.month() + 1;

  const { comparison, comparisonLoading, loadComparison } = useBudgetStore(
    useShallow((s) => ({
      comparison: s.comparison,
      comparisonLoading: s.comparisonLoading,
      loadComparison: s.loadComparison,
    })),
  );

  useEffect(() => {
    loadComparison(year, month);
  }, [year, month, loadComparison]);

  const priorDate = now.subtract(1, "month");
  const currentLabel = monthLabel(year, month);
  const priorLabel = monthLabel(priorDate.year(), priorDate.month() + 1);

  return (
    <div className="rounded-2xl bg-card border border-border p-4 sm:p-6 shadow-card">
      <div className="flex items-center gap-2 mb-1">
        <GitCompare className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-[15px] font-semibold text-foreground">
          Month Comparison
        </h2>
      </div>
      <p className="text-[13px] text-muted-foreground mb-5">
        {currentLabel} vs {priorLabel}
      </p>

      {comparisonLoading && (
        <SkeletonRegion className="py-2">
          {Array.from({ length: 4 }, (_, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2.5 sm:gap-x-4 py-2 border-b border-border"
            >
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </SkeletonRegion>
      )}

      {!comparisonLoading && comparison?.prior === null && (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <GitCompare className="w-8 h-8 text-muted-foreground/40" />
          <p className="text-[13px] text-muted-foreground text-center">
            No data for {priorLabel} — nothing to compare yet.
          </p>
        </div>
      )}

      {!comparisonLoading &&
        comparison?.prior !== null &&
        comparison !== null && (
          <div className="space-y-5">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2.5 sm:gap-x-4 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              <span>Category</span>
              <span className="text-right">{currentLabel}</span>
              <span className="text-right">{priorLabel}</span>
              <span className="text-right">Change</span>
            </div>

            {/* Overall row */}
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2.5 sm:gap-x-4 items-center py-2 border-b border-border">
              <span className="text-[13px] font-semibold text-foreground">
                Overall
              </span>
              <span className="text-[13px] font-semibold text-foreground text-right [font-variant-numeric:tabular-nums]">
                ฿{fmt(comparison.current?.overallSpent ?? 0)}
                {comparison.current && (
                  <span className="hidden sm:inline text-[11px] font-normal text-muted-foreground ml-1">
                    / ฿{fmt(comparison.current.overallBudget)}
                  </span>
                )}
              </span>
              <span className="text-[13px] font-semibold text-foreground text-right [font-variant-numeric:tabular-nums]">
                ฿{fmt(comparison.prior.overallSpent)}
                <span className="hidden sm:inline text-[11px] font-normal text-muted-foreground ml-1">
                  / ฿{fmt(comparison.prior.overallBudget)}
                </span>
              </span>
              <span className="text-right">
                {comparison.overallDelta ? (
                  <DeltaBadge
                    delta={comparison.overallDelta.delta}
                    direction={comparison.overallDelta.direction}
                  />
                ) : (
                  <span className="text-[12px] text-muted-foreground">—</span>
                )}
              </span>
            </div>

            {/* Per-category rows */}
            {comparison.categoryDeltas.length > 0 && (
              <div className="flex flex-col gap-3">
                {comparison.categoryDeltas.map((item) => (
                  <div
                    key={item.category}
                    className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2.5 sm:gap-x-4 items-center"
                  >
                    <span className="text-[13px] text-foreground">
                      {CATEGORY_LABELS[item.category] ?? item.category}
                    </span>
                    <span className="text-[13px] text-foreground text-right [font-variant-numeric:tabular-nums]">
                      ฿{fmt(item.currentSpent)}
                    </span>
                    <span className="text-[13px] text-muted-foreground text-right [font-variant-numeric:tabular-nums]">
                      ฿{fmt(item.priorSpent)}
                    </span>
                    <span className="text-right">
                      <DeltaBadge
                        delta={item.delta}
                        direction={item.direction}
                      />
                    </span>
                  </div>
                ))}
              </div>
            )}

            {comparison.categoryDeltas.length === 0 && (
              <p className="text-[13px] text-muted-foreground text-center py-4">
                No category spending data to compare.
              </p>
            )}
          </div>
        )}
    </div>
  );
}
